import React, { useReducer, useEffect, useCallback, useState } from 'react';
import { InfoCircleFilled } from '@ant-design/icons';
import NiceModal, { antdModalV5, useModal } from '@ebay/nice-modal-react';
import { Alert, Modal, Tabs, Tag } from 'antd';
import produce from 'immer';
import _capitalize from 'lodash/capitalize';
import { useCampaignList } from 'src/api/campaigns';
import { TableMainText, TableSecondaryText } from 'styles';

import { SMALL_PAGE_SIZE } from 'appConstants';
import { TableTimestamp, SearchBar, StatusTag } from 'components';
import { TableWithNoWrapHeader } from 'components/StyledTable';
import { TCampaignCreatePage } from 'containers/Campaign/i18n/TModules';
import { IAnyCampaign } from 'containers/Campaign/types';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

interface CampaignSelectModalState {
  state: any;
  page: any;
  order: any;
  size: any;
  sort_by: any;
  search_string: any;
  campaign_type: any;
  eligible_user_account_id: any;
}
const paramsReducer = (state: CampaignSelectModalState, action): CampaignSelectModalState =>
  produce(state, draft => {
    switch (action.type) {
      case 'CAMPAIGN_TYPE':
        // VS-4423
        if (action.value === 'quiz' || action.value === 'survey') {
          // if quiz or survey is selected
          draft['campaign_type'] = 'game';
          draft['game_type'] = action.value;
        } else {
          // other campaign types
          draft['campaign_type'] = action.value;
          delete draft['game_type'];
        }
        break;
      case 'STATE':
        draft.state = action.value === 'all' ? null : action.value;
        break;
      case 'TABLE':
        draft.page = action.pagination;
        if (action.sorter?.order && action.sorter?.columnKey) {
          draft.sort_by = action.sorter.columnKey;
          draft.order = action.sorter.order === 'ascend' ? 'asc' : 'desc';
        } else {
          draft.sort_by = null;
          draft.order = null;
        }

        if (action.size) {
          draft.size = action.size;
        }

        break;
      case 'SEARCH':
        draft['search_string'] = action.value;
        break;
      default:
        break;
    }
  });

interface CampaignSelectModalProps {
  campaignType?: string[] | string | null;
  hasMultiRowSelect?: boolean;
  tabs?: string[];
  eligableUserAccountId?: number;
  alreadyChosenCampaignsKeys?: number[];
  otherParams?: Record<any, any>;
  showWarning?: boolean;
  warningText?: string;
}

const CampaignSelectModal = NiceModal.create(
  ({
    campaignType = null,
    hasMultiRowSelect = false,
    tabs,
    eligableUserAccountId = null,
    alreadyChosenCampaignsKeys = [],
    otherParams = {},
    showWarning = false,
    warningText = ''
  }: CampaignSelectModalProps) => {
    const modal = useModal();
    const [selectedCampaignKeys, setSelectedCampaignKeys] = useState(alreadyChosenCampaignsKeys);
    const [currentCampaignIds, setCurrentCampaignIds] = useState([]);
    const [params, paramsDispatch] = useReducer(paramsReducer, {
      state: !tabs ? null : tabs[0] === 'all' ? null : tabs[0],
      page: 1,
      order: 'asc',
      sort_by: null,
      search_string: '',
      size: SMALL_PAGE_SIZE,
      campaign_type: campaignType,
      eligible_user_account_id: eligableUserAccountId,
      ...otherParams
    });
    const { data, meta, isLoading } = useCampaignList(params);
    const { t } = useMultiLanguageTranslation();

    const onTableChange = (pagination, filters, sorter) => {
      paramsDispatch({ type: 'TABLE', pagination: pagination.current, sorter });
    };

    const onPageSizeChange = (current, pageSize) => {
      paramsDispatch({ type: 'TABLE', pagination: current, size: pageSize });
    };

    const onTabChange = tab => {
      paramsDispatch({ type: 'STATE', value: tab });
      paramsDispatch({ type: 'TABLE', pagination: 1 });
    };

    const onSelectCampaignChange = useCallback(
      selectedCampaignKeys => {
        setSelectedCampaignKeys(prev => {
          // Remove any campaign ids from the current page to avoid duplicates
          const prevSelected = prev.filter(campaignId => !currentCampaignIds.includes(campaignId));
          return [...prevSelected, ...selectedCampaignKeys];
        });
      },
      [currentCampaignIds]
    );

    useEffect(() => {
      paramsDispatch({ type: 'CAMPAIGN_TYPE', value: campaignType });
    }, [campaignType]);

    useEffect(() => {
      const apiResponse = data;
      if (apiResponse && Array.isArray(apiResponse)) {
        const ids = apiResponse?.map(campaign => campaign.id);
        setCurrentCampaignIds(ids);
      }
    }, [data]);

    const handleOk = () => {
      if (hasMultiRowSelect) {
        modal.resolve(selectedCampaignKeys);
        modal.hide();
      } else {
        modal.resolve();
        modal.hide();
      }
    };

    return (
      <Modal
        {...antdModalV5(modal)}
        onOk={handleOk}
        maskClosable={false}
        width={1200}
        title={t(`${TCampaignCreatePage}.CampaignMechanics.CampaignSelectModal.Title`)}
        okText={t(`${TCampaignCreatePage}.CampaignMechanics.CampaignSelectModal.OkText`)}
        cancelText={t(`${TCampaignCreatePage}.CampaignMechanics.CampaignSelectModal.CancelText`)}
      >
        <div data-testid="CampaignSelectModal">
          <SearchBar
            onSearch={value => paramsDispatch({ type: 'SEARCH', value })}
            placeholder={t(
              `${TCampaignCreatePage}.CampaignMechanics.CampaignSelectModal.SearchPlaceholder`
            )}
            style={{ marginBottom: '0.5rem' }}
          />
          {showWarning && (
            <Alert
              message={warningText}
              type="info"
              icon={<InfoCircleFilled />}
              showIcon
              style={{ border: 'none', marginBottom: '10px' }}
            />
          )}
          {tabs && (
            <Tabs
              activeKey={!params.state ? 'all' : params.state}
              onChange={onTabChange}
              tabBarStyle={{ marginBottom: 0 }}
              items={tabs.map(tab => ({
                key: tab,
                label: t(
                  `${TCampaignCreatePage}.CampaignMechanics.CampaignSelectModal.${_capitalize(
                    tab === 'approved' ? 'scheduled' : tab
                  )}`
                )
              }))}
            />
          )}
          <TableWithNoWrapHeader
            scroll={{ y: 430 }}
            rowKey={'id'}
            loading={isLoading}
            columns={[
              {
                title: t(`${TCampaignCreatePage}.CampaignMechanics.CampaignSelectModal.Name`),
                key: 'name',
                render: (_, record: IAnyCampaign) => (
                  <>
                    <TableMainText>{record.name}</TableMainText>
                    <TableSecondaryText>
                      {t(`${TCampaignCreatePage}.CampaignMechanics.CampaignSelectModal.ID`)}:{' '}
                      {record.id}
                    </TableSecondaryText>
                  </>
                )
              },
              {
                title: t(`${TCampaignCreatePage}.CampaignMechanics.CampaignSelectModal.Status`),
                key: 'state',
                dataIndex: 'state',
                render: state => <StatusTag state={state} />
              },
              {
                title: t(`${TCampaignCreatePage}.CampaignMechanics.CampaignSelectModal.Tags`),
                key: 'tags',
                render: (_, record: IAnyCampaign) => (
                  <>
                    {record.tags?.map(tag => (
                      <Tag key={tag.id}>{tag.name}</Tag>
                    ))}
                  </>
                )
              },
              {
                title: t(`${TCampaignCreatePage}.CampaignMechanics.CampaignSelectModal.StartDate`),
                key: 'start_date',
                render: (_, record) => <TableTimestamp timestamp={record['start_date']} />
              },
              {
                title: t(`${TCampaignCreatePage}.CampaignMechanics.CampaignSelectModal.EndDate`),
                key: 'end_date',
                render: (_, record) => <TableTimestamp timestamp={record['end_date']} />
              }
            ]}
            dataSource={data || []}
            onChange={onTableChange}
            onRow={
              !hasMultiRowSelect
                ? (record, _) => ({
                    onClick: () => {
                      modal.resolve(record);
                      modal.hide();
                    }
                  })
                : null
            }
            rowSelection={
              hasMultiRowSelect
                ? {
                    selectedRowKeys: selectedCampaignKeys,
                    onChange: onSelectCampaignChange
                  }
                : null
            }
            showHeader
            pagination={{
              showQuickJumper: true,
              current: params.page,
              position: ['bottomRight'],
              defaultPageSize: SMALL_PAGE_SIZE,
              total: meta?.total_count || 0,
              onShowSizeChange: onPageSizeChange
            }}
          />
        </div>
      </Modal>
    );
  }
);

export default CampaignSelectModal;
